import { Text } from "components/Typography/text.styled";
import {
  Container1,
  Container2,
  EmailCell,
  EmploymentTypeCell,
  ImageCell,
  NameCell,
  PhoneCell,
  Row,
  SalaryCell,
} from "./organizationalStructure.styled";
import { Color } from "config/colors";
import { CustomIcon } from "components/icon/customIcon.component";
import { useTranslation } from "react-i18next";
import { FontFamily, FontSize } from "config/font";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { OrgWorker } from "model/OrganizationalStructure";
import { useOrganizationalStructureStore } from "web-apps/company/stores/organizationalStructureStore/organizationalStructureStore";
import { useState } from "react";
import { AlertModal } from "../general/alertModal/alertModal.component";
import { SmallImage } from "../gig/gigList/gigs.styled";

type Props = {
  worker: OrgWorker;
  setClickedWorker: React.Dispatch<React.SetStateAction<number | undefined>>;
  marginLeft?: number;
  unassigned?: boolean;
  workgroupId?: number;
  setShowInfo?: React.Dispatch<React.SetStateAction<boolean>>;
  setError?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const WorkerRow: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [state] = useCompanyStore();
  const workerImage = state.companyWorkerImages?.[props.worker.externalId];
  const [companyState] = useCompanyStore();
  const [, organizationalStructureActions] = useOrganizationalStructureStore();
  const [showAlert, setShowAlert] = useState(false);
  const isTraineeEmployee =
    (props.worker.employmentInfo.current.employmentType ?? "").toLowerCase() ===
    "trainee";

  const unassignWorker = (workerId: number) => {
    if (companyState.company && props.workgroupId) {
      organizationalStructureActions
        .unassignWorkerFromWorkGroup(
          companyState.company.id,
          props.workgroupId,
          workerId
        )
        .then(() => {
          props.setShowInfo?.(true);
        })
        .catch(() => {
          props.setError?.(true);
          props.setShowInfo?.(true);
        });
    }
  };

  return (
    <>
      <Row
        key={props.worker.workerId}
        style={{ marginLeft: props.marginLeft }}
        isInactive={!props.worker.isActive}
      >
        <Container1>
          <ImageCell>
            {workerImage &&
              (workerImage !== "no-image" ? (
                <SmallImage
                  src={workerImage}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: Color.SeaBlue300,
                  }}
                />
              ) : (
                <CustomIcon
                  name="person"
                  size="24px"
                  color={Color.White}
                  style={{
                    cursor: "pointer",
                    margin: "0 auto",
                    padding: 6,
                    backgroundColor: Color.LightGrey03,
                    borderRadius: "50%",
                    width: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              ))}
          </ImageCell>

          <NameCell>
            <Text
              fontFamily={FontFamily.MontserratSemiBold}
              fontSize={FontSize.Standard}
            >
              {`${props.worker.firstName} ${props.worker.lastName} ${
                !props.worker.isActive ? t("Salary.Inactive") : ""
              }`}
            </Text>
            <Text fontSize={FontSize.Tiny} style={{ marginTop: 5 }}>
              {props.worker.personalIdentityNumber}
            </Text>
          </NameCell>

          <PhoneCell>
            <Text fontSize={FontSize.Small}>
              {`+${props.worker.phoneNumber}`}
            </Text>
          </PhoneCell>

          <EmailCell>
            <Text fontSize={FontSize.Small} style={{ wordBreak: "break-word" }}>
              {props.worker.email}
            </Text>
          </EmailCell>

          <EmploymentTypeCell>
            <Text
              fontSize={FontSize.Small}
              style={{
                padding: "8px 12px",
                backgroundColor: Color.LightGrey03,
                borderRadius: 5,
                textAlign: "center",
                width: "100%",
              }}
            >
              {t(
                `OrganizationalStructure.${props.worker.employmentInfo.current.employmentType}`
              )}
            </Text>
          </EmploymentTypeCell>

          <SalaryCell>
            <Text fontSize={FontSize.Small}>
              {isTraineeEmployee
                ? t("OrganizationalStructure.Unpaid")
                : `${props.worker.employmentInfo.current.salary} ${
                    props.worker.employmentInfo.current.salaryType === "Monthly"
                      ? t("OrganizationalStructure.salaryPerMonth")
                      : t("OrganizationalStructure.salaryPerHour")
                  }`}
            </Text>
          </SalaryCell>
        </Container1>

        <Container2>
          {!props.unassigned && (
            <CustomIcon
              className="user-minus"
              name="user-minus"
              size="24px"
              padding="2px"
              color={Color.DarkGrey}
              style={{ marginRight: 25 }}
              onClick={() => setShowAlert(true)}
            />
          )}
          <CustomIcon
            className="icon"
            name="settings"
            size="24px"
            padding="2px"
            color={Color.DarkGrey}
            style={{ marginRight: 25 }}
            onClick={() => props.setClickedWorker(props.worker.workerId)}
          />
        </Container2>
      </Row>
      {showAlert && (
        <AlertModal
          isOpen={showAlert}
          title={t(`OrganizationalStructure.AlertTeamTitle`, {
            name: `${props.worker.firstName} ${props.worker.lastName}`,
          })}
          message={t(`OrganizationalStructure.AlertInfoWorker`, {
            name: `${props.worker.firstName} ${props.worker.lastName}`,
          })}
          onConfirm={() => {
            unassignWorker(props.worker.workerId);
            setShowAlert(false);
          }}
          onCancel={() => setShowAlert(false)}
          confirmText={t("OrganizationalStructure.Confirm")}
          cancelText={t("OrganizationalStructure.Cancel")}
        />
      )}
    </>
  );
};
